/**
 * Sign-up self-service de Cumple (ADR-013, S3-D).
 *
 *   POST /api/v1/public/signup → crea tenant + admin user + subscription
 *                                Pro trialing por 21 dias.
 *
 * Sin auth. Tras exito, navega a /login con el mensaje del trial activo.
 */
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { ApiError } from '../api/client.js';
import { publicBillingApi, type SignupResponse } from '../api/public-billing.js';
import { config, tenantHostFor } from '../config.js';

const SLUG_RE = /^[a-z][a-z0-9-]*$/;

export function SignupPage(): JSX.Element {
  const navigate = useNavigate();
  const [organizationName, setOrganizationName] = useState('');
  const [organizationSlug, setOrganizationSlug] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [sector, setSector] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<SignupResponse | null>(null);

  function validate(): string | null {
    if (organizationName.trim().length < 2) return 'Nombre de la organizacion muy corto';
    if (!SLUG_RE.test(organizationSlug.trim()))
      return 'Identificador: minusculas, numeros y guiones, empezando con letra (ej. acme-do)';
    if (!/.+@.+\..+/.test(adminEmail.trim())) return 'Email invalido';
    if (adminPassword.length < 12) return 'La contraseña debe tener al menos 12 caracteres';
    if (!acceptTerms) return 'Debes aceptar los terminos para crear la cuenta';
    return null;
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const trimmedSector = sector.trim();
      const res = await publicBillingApi.signup({
        organization_name: organizationName.trim(),
        organization_slug: organizationSlug.trim(),
        admin_email: adminEmail.trim().toLowerCase(),
        admin_password: adminPassword,
        accept_terms: true,
        ...(trimmedSector ? { sector: trimmedSector } : {}),
      });
      setSuccess(res);
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 409) setError('Ese identificador ya esta en uso. Prueba otro.');
        else if (err.status === 400) setError('Datos invalidos. Revisa los campos.');
        else setError(`Error al crear la cuenta (${err.status})`);
      } else {
        setError('Error de red. Intenta de nuevo.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <main className="mx-auto max-w-md px-6 py-12">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 space-y-4">
          <h1 className="text-xl font-semibold text-emerald-900">¡Cuenta creada!</h1>
          <p className="text-sm text-emerald-800">
            Tu organizacion <strong>{success.tenant.name}</strong> esta lista. Tienes{' '}
            <strong>{success.trial.days_remaining} dias</strong> de trial Pro (vence el{' '}
            {new Date(success.trial.ends_at).toLocaleDateString('es-DO')}).
          </p>
          <p className="text-sm text-emerald-800">
            Te enviaremos las instrucciones a <strong>{success.admin.email}</strong>.
          </p>
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="block w-full rounded-lg bg-brand-700 px-4 py-2 text-center text-sm font-medium text-white hover:bg-brand-700/90"
          >
            Iniciar sesión
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-md px-6 py-12">
      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        className="space-y-5"
        noValidate
      >
        <header className="text-center space-y-2">
          <h1 className="text-2xl font-semibold text-brand-900">Crea tu cuenta Cumple</h1>
          <p className="text-sm text-slate-500">
            Trial Pro 21 dias sin tarjeta · cancela cuando quieras
          </p>
        </header>

        <div className="space-y-2">
          <label htmlFor="org_name" className="block text-sm font-medium text-slate-700">
            Nombre de la organizacion
          </label>
          <input
            id="org_name"
            type="text"
            required
            minLength={2}
            maxLength={200}
            value={organizationName}
            onChange={(e) => setOrganizationName(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="org_slug" className="block text-sm font-medium text-slate-700">
            Identificador (kebab-case)
          </label>
          <input
            id="org_slug"
            type="text"
            required
            pattern="^[a-z][a-z0-9-]*$"
            value={organizationSlug}
            onChange={(e) => setOrganizationSlug(e.target.value.toLowerCase())}
            placeholder="acme-do"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
          <p className="text-xs text-slate-500">
            Sera tu URL: {tenantHostFor(organizationSlug || 'tu-slug')}
          </p>
        </div>

        <div className="space-y-2">
          <label htmlFor="admin_email" className="block text-sm font-medium text-slate-700">
            Email del administrador
          </label>
          <input
            id="admin_email"
            type="email"
            required
            autoComplete="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="admin_password" className="block text-sm font-medium text-slate-700">
            Contraseña (min 12 caracteres)
          </label>
          <input
            id="admin_password"
            type="password"
            required
            minLength={12}
            autoComplete="new-password"
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="sector" className="block text-sm font-medium text-slate-700">
            Sector (opcional)
          </label>
          <input
            id="sector"
            type="text"
            maxLength={100}
            value={sector}
            onChange={(e) => setSector(e.target.value)}
            placeholder="Banca, telecomunicaciones, salud..."
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>

        <label className="flex items-start gap-3 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-300 text-brand-700 focus:ring-brand-500"
          />
          <span>
            Acepto los{' '}
            <a
              href={`${config.marketingUrl}/legal/terms`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-700 underline"
            >
              terminos de servicio
            </a>{' '}
            y la{' '}
            <a
              href={`${config.marketingUrl}/legal/privacy`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-700 underline"
            >
              política de privacidad
            </a>
            .
          </span>
        </label>

        {error && (
          <div
            role="alert"
            className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-200"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700/90 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {submitting ? 'Creando cuenta...' : 'Crear cuenta y empezar trial'}
        </button>

        <p className="text-center text-xs text-slate-500">
          ¿Ya tienes cuenta?{' '}
          <Link to="/login" className="text-brand-700 underline">
            Iniciar sesión
          </Link>
        </p>
      </form>
    </main>
  );
}
